import React from 'react'
import { getBlocksDataFromSectionData } from '@jarvis.oddle/core/tools/customisation'
import VisibilitySensor from '@jarvis.oddle/core/components/VisibilitySensor'
import { DEFAULT_BREAKPOINTS } from '@jarvis.oddle/core/env'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Carousel from '@jarvis.oddle/core/components/Carousel'
import Div from '@jarvis.oddle/core/components/Div'
import useCustomisation from 'src/hooks/useCustomisation'
import SlideOverlay from './SlideOverlay'
import { generateImageSources } from '@jarvis.oddle/core/tools/uploadcare'
import { RatioImage } from '@jarvis.oddle/core/components/Image'
import { colorConstants } from '@jarvis.oddle/core/theme/default'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectPreviewMode, selectSections } from 'src/redux/customisation'
import { selectDetailMenuSite } from 'src/redux/meta'
import { DesktopHidden, DesktopOnly } from 'src/components/core/Responsive'
import ResponsiveSection from 'src/components/core/ResponsiveSection'

const imageHeightWidthRatio = 5 / 12

const StyledResponsiveSection = styled(ResponsiveSection)`
  margin-bottom: 30px;
  margin-top: 30px;
  ${props => props.index === 0 || props.index === 1 ? `
    margin-top: 0;
  ` : ''}
`

StyledResponsiveSection.Container = styled(ResponsiveSection.Container)`
  max-width: 100vw;
  padding-left: 0;
  padding-right: 0;
`

const ResponsiveHeightContainer = styled(Div)`
  position: relative;
  width: 100%;
  ${breakpoint('desktop')`
    padding-top: ${imageHeightWidthRatio * 100}%;
  `}
`

const CarouselContainer = styled(Div)`
  ${breakpoint('desktop')`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `}
`

const SlideContainer = styled(Div)`
  position: relative;
`

const StyledRatioImage = styled(RatioImage)`
  background: ${colorConstants.lightGray};
`

interface SlideShowBannerSection {
  id: string
  name: string
  type: string
  settings?: {
    autoscroll: boolean
    autoscrollInterval: number
  }
  blocks?: {
    [key: string]: {
      id: string
      name: string
      type: string
      settings: {
        imageSource: string
        headingText: string
        buttonLabel: string
        textPosition: string
        buttonLink: string
        overlayColorDesktop: {
          r: number
          g: number
          b: number
          a: number
        }
        textColorDesktop: string
        overlayColorMobile: string
        textColorMobile: string
      }
    }
  }
  blocksOrder: string[]
  updatedAt: number
  options: {
    visibleForPreview: boolean
  }
}

interface SlideShowBannerProps {
  id: string
  index: number
}

interface carouselRefType {
  slickPlay: () => void
  slickPause: () => void
}

const defaultImageSource =
  'https://ucarecdn.com/9e2b16de-f3ef-4c4b-b0e4-c8c857d64c2f/webdefaultimagebanner3x.png'

const SlideshowBanner = React.memo(
  ({ id, index }: SlideShowBannerProps): JSX.Element => {
    const previewMode = useAppSelector(selectPreviewMode)
    const {
      menu: { name: menuName }
    } = useAppSelector(selectDetailMenuSite)
    const customisation = useAppSelector(selectSections)[
      id
    ] as SlideShowBannerSection
    const { getSectionFromId } = useCustomisation()
    const [slideIndex, setSlideIndex] = React.useState<number>(0)

    // TODO: check why this can be undefined
    const { storeName } = getSectionFromId('header').settings || {}

    const carouselRef = React.useRef<carouselRefType>()
    const { settings } = customisation
    const images = React.useMemo(
      () =>
        getBlocksDataFromSectionData(customisation).map(({ settings }) => ({
          src: settings.imageSource || defaultImageSource,
          ...settings
        })),
      [customisation]
    )

    const slideCount = images.length

    const autoplay = images.length > 1 ? settings.autoscroll : false

    const onAfterSlideChanged = React.useCallback(
      (index) => setSlideIndex(index),
      []
    )

    const setCarouselRef = React.useCallback((el) => {
      carouselRef.current = el
    }, [])

    const onVisibilityChange = React.useCallback((visible) => {
      if (carouselRef.current) {
        if (visible) {
          carouselRef.current.slickPlay()
        } else {
          carouselRef.current.slickPause()
        }
      }
    }, [])

    return (
      <VisibilitySensor
        onChange={onVisibilityChange}
        threshold={1}
        disabled={!autoplay}
      >
        <StyledResponsiveSection index={index}>
          <StyledResponsiveSection.Container>
            <ResponsiveHeightContainer>
              <CarouselContainer>
                <Carousel
                  innerRef={setCarouselRef}
                  lazyLoad
                  adaptiveHeight
                  afterChange={onAfterSlideChanged}
                  autoplay={autoplay}
                  autoplaySpeed={settings.autoscrollInterval}
                >
                  {images.map(({ src, ...overlayTextSettings }, index) => (
                    <SlideContainer key={index}>
                      <StyledRatioImage
                        {...generateImageSources({
                          url: src,
                          baseW: DEFAULT_BREAKPOINTS.desktop
                        })}
                        alt={storeName || menuName}
                        sizes='100vw'
                        ratio={imageHeightWidthRatio}
                      />
                      <DesktopOnly>
                        <SlideOverlay
                          {...overlayTextSettings}
                          previewMode={previewMode}
                          active={index === slideIndex || slideCount === 1}
                        />
                      </DesktopOnly>
                      <DesktopHidden>
                        <SlideOverlay
                          {...overlayTextSettings}
                          previewMode={previewMode}
                          active
                        />
                      </DesktopHidden>
                    </SlideContainer>
                  ))}
                </Carousel>
              </CarouselContainer>
            </ResponsiveHeightContainer>
          </StyledResponsiveSection.Container>
        </StyledResponsiveSection>
      </VisibilitySensor>
    )
  }
)

export default SlideshowBanner
